import { parseMemberContext } from "./member-context";
import { expectArray, expectBoolean, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const INVITE_SCOPE_TYPES = ["student", "family"];
export const RELATION_TYPES = ["parent", "guardian", "self"];
export const GUARDIAN_INVITE_STATUSES = ["active", "consumed", "expired", "revoked"];
export const GUARDIAN_LINK_REQUEST_STATUSES = ["submitted", "approved", "rejected", "cancelled", "completed"];
export const USER_STUDENT_LINK_STATUSES = ["active", "revoked"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
function parsePositiveIntegerOrNull(value, path) {
    if (value === null || value === undefined) {
        return null;
    }
    const parsed = expectNumber(value, path);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        throw new Error(`${path} must be a positive integer`);
    }
    return parsed;
}
export function parseInviteScopeType(value, path = "scopeType") {
    return parseStringEnum(value, INVITE_SCOPE_TYPES, path);
}
export function parseRelationType(value, path = "relationType") {
    return parseStringEnum(value, RELATION_TYPES, path);
}
export function parseGuardianInviteStatus(value, path = "inviteStatus") {
    return parseStringEnum(value, GUARDIAN_INVITE_STATUSES, path);
}
export function parseGuardianLinkRequestStatus(value, path = "guardianLinkRequestStatus") {
    return parseStringEnum(value, GUARDIAN_LINK_REQUEST_STATUSES, path);
}
export function parseUserStudentLinkStatus(value, path = "userStudentLinkStatus") {
    return parseStringEnum(value, USER_STUDENT_LINK_STATUSES, path);
}
export function parseGuardianInviteStudent(value, path = "guardianInviteStudent") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        displayName: expectString(record.displayName, `${path}.displayName`)
    };
}
export function parseGuardianInviteDeliveryArtifacts(value, path = "guardianInviteDeliveryArtifacts") {
    const record = expectRecord(value, path);
    return {
        manualCode: expectString(record.manualCode, `${path}.manualCode`),
        inviteUrl: expectString(record.inviteUrl, `${path}.inviteUrl`)
    };
}
export function parseAdminGuardianInvite(value, path = "adminGuardianInvite") {
    const record = expectRecord(value, path);
    return {
        inviteId: expectString(record.inviteId, `${path}.inviteId`),
        scopeType: parseInviteScopeType(record.scopeType, `${path}.scopeType`),
        relationTypeDefault: parseRelationType(record.relationTypeDefault, `${path}.relationTypeDefault`),
        status: parseGuardianInviteStatus(record.status, `${path}.status`),
        studentIds: expectArray(record.studentIds, expectString, `${path}.studentIds`),
        students: expectArray(record.students, parseGuardianInviteStudent, `${path}.students`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        familyAccountDisplayName: expectOptionalString(record.familyAccountDisplayName, `${path}.familyAccountDisplayName`),
        maxUseCount: expectNumber(record.maxUseCount, `${path}.maxUseCount`),
        usedCount: expectNumber(record.usedCount, `${path}.usedCount`),
        remainingUseCount: expectNumber(record.remainingUseCount, `${path}.remainingUseCount`),
        activeLinkCount: expectNumber(record.activeLinkCount, `${path}.activeLinkCount`),
        activeLinkedStudentIds: expectArray(record.activeLinkedStudentIds, expectString, `${path}.activeLinkedStudentIds`),
        activeLinkedStudents: expectArray(record.activeLinkedStudents, parseGuardianInviteStudent, `${path}.activeLinkedStudents`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`),
        lastConsumedAt: expectOptionalString(record.lastConsumedAt, `${path}.lastConsumedAt`),
        createdByPlatformUserId: expectOptionalString(record.createdByPlatformUserId, `${path}.createdByPlatformUserId`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseAdminGuardianInvitesResponse(value, path = "adminGuardianInvitesResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminGuardianInvite, `${path}.items`)
    };
}
export function parseAdminGuardianLink(value, path = "adminGuardianLink") {
    const record = expectRecord(value, path);
    const sourceType = expectString(record.sourceType, `${path}.sourceType`);
    if (sourceType !== "invite" && sourceType !== "adminApproval") {
        throw new Error(`${path}.sourceType must be one of: invite, adminApproval`);
    }
    return {
        linkId: expectString(record.linkId, `${path}.linkId`),
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        guardianDisplayName: expectOptionalString(record.guardianDisplayName, `${path}.guardianDisplayName`),
        relationType: parseRelationType(record.relationType, `${path}.relationType`),
        status: parseUserStudentLinkStatus(record.status, `${path}.status`),
        sourceType,
        sourceInviteId: expectOptionalString(record.sourceInviteId, `${path}.sourceInviteId`),
        sourceRequestId: expectOptionalString(record.sourceRequestId, `${path}.sourceRequestId`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`),
        revokedAt: expectOptionalString(record.revokedAt, `${path}.revokedAt`),
        revokedByPlatformUserId: expectOptionalString(record.revokedByPlatformUserId, `${path}.revokedByPlatformUserId`)
    };
}
export function parseAdminGuardianLinksResponse(value, path = "adminGuardianLinksResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminGuardianLink, `${path}.items`)
    };
}
export function parseCreateAdminGuardianInviteRequest(value, path = "createAdminGuardianInviteRequest") {
    const record = expectRecord(value, path);
    const scopeType = parseInviteScopeType(record.scopeType, `${path}.scopeType`);
    const studentId = expectOptionalString(record.studentId, `${path}.studentId`);
    const familyAccountId = expectOptionalString(record.familyAccountId, `${path}.familyAccountId`);
    const relationTypeDefaultRaw = hasOwn(record, "relationTypeDefault")
        ? expectOptionalString(record.relationTypeDefault, `${path}.relationTypeDefault`)
        : null;
    if (scopeType === "student" && !studentId) {
        throw new Error(`${path}.studentId is required when scopeType=student`);
    }
    if (scopeType === "family" && !familyAccountId) {
        throw new Error(`${path}.familyAccountId is required when scopeType=family`);
    }
    if (relationTypeDefaultRaw !== null &&
        relationTypeDefaultRaw !== "parent" &&
        relationTypeDefaultRaw !== "guardian") {
        throw new Error(`${path}.relationTypeDefault must be one of: parent, guardian`);
    }
    return {
        scopeType,
        studentId,
        familyAccountId,
        relationTypeDefault: relationTypeDefaultRaw,
        maxUseCount: parsePositiveIntegerOrNull(record.maxUseCount, `${path}.maxUseCount`),
        expiresAt: expectOptionalString(record.expiresAt, `${path}.expiresAt`)
    };
}
export function parseCreateAdminGuardianInviteResponse(value, path = "createAdminGuardianInviteResponse") {
    const record = expectRecord(value, path);
    return {
        invite: parseAdminGuardianInvite(record.invite, `${path}.invite`),
        delivery: parseGuardianInviteDeliveryArtifacts(record.delivery, `${path}.delivery`)
    };
}
export function parseResolveGuardianInviteRequest(value, path = "resolveGuardianInviteRequest") {
    const record = expectRecord(value, path);
    const inviteToken = expectOptionalString(record.inviteToken, `${path}.inviteToken`);
    const manualCode = expectOptionalString(record.manualCode, `${path}.manualCode`);
    if (!inviteToken && !manualCode) {
        throw new Error(`${path} requires inviteToken or manualCode`);
    }
    return {
        inviteToken,
        manualCode
    };
}
export function parseResolvedGuardianInvite(value, path = "resolvedGuardianInvite") {
    const record = expectRecord(value, path);
    return {
        inviteId: expectString(record.inviteId, `${path}.inviteId`),
        scopeType: parseInviteScopeType(record.scopeType, `${path}.scopeType`),
        relationTypeDefault: parseRelationType(record.relationTypeDefault, `${path}.relationTypeDefault`),
        status: parseGuardianInviteStatus(record.status, `${path}.status`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`),
        maxUseCount: expectNumber(record.maxUseCount, `${path}.maxUseCount`),
        usedCount: expectNumber(record.usedCount, `${path}.usedCount`),
        remainingUseCount: expectNumber(record.remainingUseCount, `${path}.remainingUseCount`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        students: expectArray(record.students, parseGuardianInviteStudent, `${path}.students`),
        alreadyConsumedByCurrentUser: expectBoolean(record.alreadyConsumedByCurrentUser, `${path}.alreadyConsumedByCurrentUser`)
    };
}
export function parseConsumeGuardianInviteRequest(value, path = "consumeGuardianInviteRequest") {
    const record = expectRecord(value, path);
    return {
        inviteId: expectString(record.inviteId, `${path}.inviteId`)
    };
}
export function parseConsumeGuardianInviteResponse(value, path = "consumeGuardianInviteResponse") {
    const record = expectRecord(value, path);
    return {
        inviteId: expectString(record.inviteId, `${path}.inviteId`),
        alreadyConsumed: expectBoolean(record.alreadyConsumed, `${path}.alreadyConsumed`),
        linkedStudentIds: expectArray(record.linkedStudentIds, expectString, `${path}.linkedStudentIds`),
        createdLinkIds: expectArray(record.createdLinkIds, expectString, `${path}.createdLinkIds`),
        member: parseMemberContext(record.member, `${path}.member`)
    };
}
export function parseCreateGuardianLinkRequest(value, path = "createGuardianLinkRequest") {
    const record = expectRecord(value, path);
    const requestedRelationType = parseRelationType(record.requestedRelationType, `${path}.requestedRelationType`);
    if (requestedRelationType === "self") {
        throw new Error(`${path}.requestedRelationType must be one of: parent, guardian`);
    }
    return {
        requestedRelationType,
        studentName: expectString(record.studentName, `${path}.studentName`),
        birthDate: expectOptionalString(record.birthDate, `${path}.birthDate`),
        supportingInfo: expectOptionalString(record.supportingInfo, `${path}.supportingInfo`)
    };
}
export function parseApproveGuardianLinkRequestRequest(value, path = "approveGuardianLinkRequestRequest") {
    const record = expectRecord(value, path);
    return {
        studentIds: hasOwn(record, "studentIds")
            ? expectArray(record.studentIds, expectString, `${path}.studentIds`)
            : null,
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        internalNote: expectOptionalString(record.internalNote, `${path}.internalNote`)
    };
}
export function parseGuardianLinkRequest(value, path = "guardianLinkRequest") {
    const record = expectRecord(value, path);
    return {
        requestId: expectString(record.requestId, `${path}.requestId`),
        requesterPlatformUserId: expectString(record.requesterPlatformUserId, `${path}.requesterPlatformUserId`),
        requestedRelationType: parseRelationType(record.requestedRelationType, `${path}.requestedRelationType`),
        studentName: expectString(record.studentName, `${path}.studentName`),
        birthDate: expectOptionalString(record.birthDate, `${path}.birthDate`),
        supportingInfo: expectOptionalString(record.supportingInfo, `${path}.supportingInfo`),
        status: parseGuardianLinkRequestStatus(record.status, `${path}.status`),
        resolvedStudentIds: expectArray(record.resolvedStudentIds, expectString, `${path}.resolvedStudentIds`),
        resolvedFamilyAccountId: expectOptionalString(record.resolvedFamilyAccountId, `${path}.resolvedFamilyAccountId`),
        reviewedByPlatformUserId: expectOptionalString(record.reviewedByPlatformUserId, `${path}.reviewedByPlatformUserId`),
        reviewedAt: expectOptionalString(record.reviewedAt, `${path}.reviewedAt`),
        internalNote: expectOptionalString(record.internalNote, `${path}.internalNote`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseGuardianLinkRequestListResponse(value, path = "guardianLinkRequestListResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseGuardianLinkRequest, `${path}.items`)
    };
}
